// Marker: scan a list of drones first, then pick one action from the registry, fill the inputs
// it asks for, confirm once, and read "n done, m skipped" with the reason for each skip. A
// server that answers needs_confirm gets one "Not expected here" panel, and Accept re-sends the
// batch with confirm: true. The flow knows nothing about any one action; markerActions.tsx
// carries them.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { INSTANCE, type Instance } from '../../instance';
import { BTN_TONAL, FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import { shipWarnings, warningDetail } from '../../Sales/shipping';
import type { ShipWarning } from '../../Sales/shipping';
import useUserStore from '../../stores/useUserStore';
import { BackIcon, CheckIcon, OpenIcon, WarningIcon } from '../icons';
import { buzz, inShell } from '../shell';
import type { ToolViewer } from '../types';
import { BAR_KEY, Notice } from '../ui';
import { actionsFor, MARKER_ACTIONS } from './markerActions';
import { MarkerInputField } from './MarkerInputs';
import MarkerScan from './MarkerScan';
import type { MarkedDrone, MarkerAction, MarkerResult, MarkerRunOptions, MarkerValues } from './types';

type Step =
  | { name: 'scan' }
  | { name: 'action' }
  | { name: 'inputs'; action: MarkerAction }
  | { name: 'confirm'; action: MarkerAction }
  | { name: 'warn'; action: MarkerAction; warnings: ShipWarning[] }
  | { name: 'result'; action: MarkerAction; result: MarkerResult };

interface Props {
  /** The badged operator; sent as operator_id only on a shared device. */
  operatorId?: string | null;
  isSharedMode?: boolean;
  setLeaveGuard?: (guard: ((leave: () => void) => boolean) | null) => void;
  /** Who is holding the scanner. Defaults to the signed-in user, or the kiosk's token set. */
  viewer?: ToolViewer;
  instance?: Instance;
  /** The registry to offer from. Defaults to MARKER_ACTIONS; a test passes its own. */
  actions?: MarkerAction[];
  /** When offered, the scan step's Next opens this action instead of the action list. */
  initialActionId?: string;
}

const drones = (n: number) => `${n} ${n === 1 ? 'drone' : 'drones'}`;
const TYPE_WORD: Record<string, string> = { sale: 'Sale', lease: 'Lease', rental: 'Rental' };
// These codes carry a whole sentence in `expected`; the rest read through the Ship tool's words.
const SENTENCE_CODES = ['out_twice', 'no_open_line', 'record_disagrees'];
const warningText = (w: ShipWarning) => (SENTENCE_CODES.includes(w.code) ? w.expected : warningDetail(w));
const PRIMARY = `min-h-[3.25rem] w-full rounded-full bg-primary px-5 text-base font-bold text-on-primary np-state np-state-on-primary disabled:opacity-40 ${FOCUS} ${MOTION}`;

const StepBar: React.FC<{ backLabel: string; onBack: () => void; title: string }> = ({ backLabel, onBack, title }) => (
  <div className="flex h-12 shrink-0 items-center gap-2 px-2">
    <button type="button" onClick={onBack} className={`${BAR_KEY} pl-1.5 pr-3 text-on-surface-variant hover:text-on-surface`}>
      <BackIcon className="h-5 w-5" />
      {backLabel}
    </button>
    <p className="min-w-0 truncate text-base font-semibold text-on-surface">{title}</p>
  </div>
);

/** Asked before the list is left. Keep scanning is the default and holds focus. */
const DiscardPanel: React.FC<{ count: number; onKeep: () => void; onDiscard: () => void }> = ({ count, onKeep, onDiscard }) => {
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  const title = `Discard ${drones(count)}?`;
  return (
    <div className="absolute inset-0 z-10 flex items-end bg-[rgb(25_25_25/0.8)] px-4 pb-4" onKeyDown={(e) => e.key === 'Escape' && onKeep()}>
      <div role="alertdialog" aria-modal="true" aria-label={title} className="mx-auto flex w-full max-w-md flex-col gap-3 rounded-xl bg-surface-container-high p-3">
        <p className="flex items-center gap-2 text-base font-semibold text-red-100">
          <WarningIcon className="h-5 w-5 shrink-0" />
          {title}
        </p>
        <p className="text-sm text-on-surface-variant">Leaving drops the list. Nothing has been marked.</p>
        <button ref={keepRef} type="button" onClick={onKeep} className={PRIMARY}>Keep scanning</button>
        <button type="button" onClick={onDiscard} className={`${BTN_TONAL} min-h-[2.75rem] w-full justify-center`}>Discard and leave</button>
      </div>
    </div>
  );
};

const ResultView: React.FC<{ result: MarkerResult; words?: MarkerAction['words']; onMore: () => void }> = ({ result, words, onMore }) => {
  const skipped = [
    ...result.unchanged.map((serial) => ({ serial, why: words?.unchanged ?? 'already set' })),
    ...result.missing.map((serial) => ({ serial, why: 'not found' })),
  ];
  const headline = words
    ? [
        `${result.updated.length} ${words.done}`,
        `${result.unchanged.length} ${words.unchanged}`,
        ...(result.missing.length > 0 ? [`${result.missing.length} not found`] : []),
      ].join(', ')
    : `${result.updated.length} done, ${skipped.length} skipped`;
  const orders = result.orders ?? [];
  return (
    <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-6">
        <div className="flex flex-col items-center gap-2 rounded-xl bg-surface-container-low p-6 text-center motion-safe:animate-rowLand">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
            <CheckIcon className="h-8 w-8" />
          </span>
          <p className="text-xl font-semibold tabular-nums text-on-surface" role="status">
            {headline}
          </p>
          {result.owner_name && <p className="text-base text-on-surface-variant">Marked to {result.owner_name}</p>}
        </div>

        {orders.length > 0 && (
          <section aria-label="Returned on" className="rounded-xl bg-surface-container-low p-3">
            <h2 className="mb-2 text-sm font-semibold text-on-surface-variant">Returned on</h2>
            <ul className="flex flex-col gap-1.5">
              {orders.map((o) => (
                <li key={o.so_number} className="flex min-h-[2.75rem] items-center gap-3 rounded-lg bg-surface-container px-3 py-1.5">
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="whitespace-nowrap font-mono text-base tabular-nums text-on-surface">{o.so_number}</span>
                    <span className="whitespace-nowrap rounded-full bg-surface-container-highest px-2 py-0.5 text-xs font-medium text-on-surface-variant">
                      {TYPE_WORD[o.order_type] ?? o.order_type}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block whitespace-nowrap text-base font-semibold tabular-nums text-on-surface">{drones(o.count)}</span>
                    {o.status && <span className="block whitespace-nowrap text-sm text-on-surface-variant">{o.status.replace(/_/g, ' ')}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {skipped.length > 0 && (
          <section aria-label="Skipped drones" className="rounded-xl bg-surface-container-low p-3">
            <h2 className="mb-2 text-sm font-semibold text-on-surface-variant">Skipped</h2>
            <ul className="flex flex-col gap-1.5">
              {skipped.map((s) => (
                <li key={`${s.serial}-${s.why}`} className="flex min-h-[2.75rem] items-center justify-between gap-3 rounded-lg bg-surface-container px-3">
                  <span className="truncate font-mono text-base text-on-surface">{s.serial}</span>
                  <span className="shrink-0 text-sm text-on-surface-variant">{s.why}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {result.unarchived.length > 0 && (
          <section aria-label="Brought back from the archive" className="rounded-xl bg-amber-900/30 p-3">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-200">
              <WarningIcon className="h-5 w-5 shrink-0" />
              Brought back from the archive
            </h2>
            <ul className="flex flex-col gap-1">
              {result.unarchived.map((serial) => (
                <li key={serial} className="truncate font-mono text-base text-on-surface">{serial}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <div className="shrink-0 bg-surface px-4 pb-4 pt-2">
        <button type="button" onClick={onMore} className={PRIMARY}>Mark more</button>
      </div>
    </div>
  );
};

const MarkerFlow: React.FC<Props> = ({
  operatorId = null,
  isSharedMode = false,
  setLeaveGuard,
  viewer,
  instance = INSTANCE,
  actions = MARKER_ACTIONS,
  initialActionId,
}) => {
  const api = useApi();
  const user = useUserStore((s: any) => s.user);
  const who = viewer ?? { kiosk: inShell(), user };
  const offered = useMemo(
    () => actionsFor(who, instance, actions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [who.kiosk, who.user, instance, actions],
  );

  const [step, setStep] = useState<Step>({ name: 'scan' });
  // The action a shortcut opened Marker on. Picking from the list switches it off for the session.
  const [shortcutId, setShortcutId] = useState<string | undefined>(initialActionId);
  const [list, setList] = useState<MarkedDrone[]>([]);
  const [values, setValues] = useState<MarkerValues>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const pendingLeave = useRef<(() => void) | null>(null);
  const dirtyRef = useRef(false);
  dirtyRef.current = list.length > 0 && step.name !== 'result';
  const busyRef = useRef(false);
  busyRef.current = busy;

  useEffect(() => {
    if (!setLeaveGuard) return undefined;
    setLeaveGuard((leave) => {
      // Mid-commit the write lands whatever the operator picks, so stay for the result.
      if (busyRef.current) return false;
      if (!dirtyRef.current) return true;
      pendingLeave.current = leave;
      setAsking(true);
      return false;
    });
    return () => setLeaveGuard(null);
  }, [setLeaveGuard]);

  const add = (d: MarkedDrone) => setList((prev) => (prev.some((x) => x.serial === d.serial) ? prev : [...prev, d]));
  const remove = (serial: string) => setList((prev) => prev.filter((d) => d.serial !== serial));

  const startOver = () => {
    setList([]);
    setValues({});
    setLabels({});
    setError(null);
    setStep({ name: 'scan' });
  };

  const choose = (action: MarkerAction) => {
    setValues({});
    setLabels({});
    setError(null);
    setStep({ name: action.inputs.length > 0 ? 'inputs' : 'confirm', action } as Step);
  };

  const visible = (action: MarkerAction) => action.inputs.filter((i) => !i.when || i.when(values));
  const complete = (action: MarkerAction) => visible(action).every((i) => values[i.key] !== undefined);

  const run = async (action: MarkerAction, opts: MarkerRunOptions = {}) => {
    setBusy(true);
    setError(null);
    try {
      const shown = Object.fromEntries(visible(action).map((i) => [i.key, values[i.key]]));
      const result = await action.run(api, list.map((d) => d.serial), shown, isSharedMode && operatorId ? operatorId : null, opts);
      setStep({ name: 'result', action, result });
    } catch (e: any) {
      const warnings = shipWarnings(e);
      if (warnings) {
        buzz('warn');
        setStep({ name: 'warn', action, warnings });
        return;
      }
      buzz('reject');
      setError(`${e?.message || 'The marks were not saved.'} The list is kept. Try again.`);
    } finally {
      setBusy(false);
    }
  };

  const archived = list.filter((d) => d.archived).length;
  const guarded = (node: React.ReactNode) => (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {node}
      {asking && (
        <DiscardPanel
          count={list.length}
          onKeep={() => {
            pendingLeave.current = null;
            setAsking(false);
          }}
          onDiscard={() => {
            const leave = pendingLeave.current;
            pendingLeave.current = null;
            setAsking(false);
            if (leave) leave();
          }}
        />
      )}
    </div>
  );

  if (step.name === 'scan') {
    const initial = shortcutId ? offered.find((a) => a.id === shortcutId) : undefined;
    return guarded(
      <MarkerScan
        drones={list}
        onAdd={add}
        onRemove={remove}
        nextLabel={initial ? `${initial.label}: ${drones(list.length)}` : undefined}
        onNext={() => (initial ? choose(initial) : setStep({ name: 'action' }))}
      />,
    );
  }

  if (step.name === 'action') {
    return guarded(
      <div className="mx-auto w-full max-w-md flex-1 overflow-y-auto pb-6">
        <StepBar backLabel="Scan" onBack={() => setStep({ name: 'scan' })} title={`Mark ${drones(list.length)}`} />
        <div className="px-4 pt-2">
          {offered.length === 0 ? (
            <p className="text-base text-on-surface-variant">
              Nothing here is open to your account. Ask an admin for operations access.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {offered.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setShortcutId(undefined);
                      choose(a);
                    }}
                    className={`flex min-h-[4.5rem] w-full items-center gap-4 rounded-xl bg-surface-container px-4 py-3 text-left np-state ${FOCUS} ${MOTION}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-lg font-semibold text-on-surface">{a.label}</span>
                      <span className="block text-sm text-on-surface-variant">{a.description}</span>
                    </span>
                    <OpenIcon className="h-5 w-5 shrink-0 text-on-surface-variant" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>,
    );
  }

  if (step.name === 'inputs') {
    const action = step.action;
    return guarded(
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
        <StepBar backLabel="Actions" onBack={() => setStep({ name: 'action' })} title={action.label} />
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-3 pt-2">
          {visible(action).map((input) => (
            <MarkerInputField
              key={input.key}
              input={input}
              value={values[input.key]}
              onChange={(v, label) => {
                setValues((prev) => ({ ...prev, [input.key]: v }));
                setLabels((prev) => ({ ...prev, [input.key]: label }));
              }}
            />
          ))}
        </div>
        <div className="shrink-0 bg-surface px-4 pb-4 pt-2">
          <button type="button" disabled={!complete(action)} onClick={() => setStep({ name: 'confirm', action })} className={PRIMARY}>
            Review
          </button>
        </div>
      </div>,
    );
  }

  if (step.name === 'confirm') {
    const action = step.action;
    const parts = visible(action).map((i) => labels[i.key]).filter(Boolean);
    const sentence = `Mark ${drones(list.length)}${parts.length > 0 ? `: ${parts.join(', ')}` : ''}`;
    return guarded(
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
        <StepBar
          backLabel={action.inputs.length > 0 ? 'Change' : 'Actions'}
          onBack={() => setStep(action.inputs.length > 0 ? { name: 'inputs', action } : { name: 'action' })}
          title={action.label}
        />
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pt-2">
          <p className="rounded-xl bg-surface-container-low p-4 text-xl font-semibold text-on-surface">{sentence}</p>
          {archived > 0 && (
            <p className="flex items-center gap-2 rounded-xl bg-amber-900/30 px-3 py-2.5 text-base text-amber-200">
              <WarningIcon className="h-5 w-5 shrink-0" />
              {archived === 1 ? '1 archived drone is' : `${archived} archived drones are`} brought back first.
            </p>
          )}
          {error && (
            <div role="alert">
              <Notice tone="miss">{error}</Notice>
            </div>
          )}
        </div>
        <div className="shrink-0 bg-surface px-4 pb-4 pt-2">
          <button type="button" disabled={busy} onClick={() => run(action)} className={PRIMARY}>
            {busy ? 'Marking…' : `Mark ${drones(list.length)}`}
          </button>
        </div>
      </div>,
    );
  }

  if (step.name === 'warn') {
    const { action, warnings } = step;
    return guarded(
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pt-4">
          <div role="alert" className="flex flex-col gap-3 rounded-xl bg-amber-900/30 p-4">
            <p className="flex items-center gap-2 text-xl font-semibold text-amber-200">
              <WarningIcon className="h-6 w-6 shrink-0" />
              Not expected here
            </p>
            <p className="text-base text-on-surface">The record will be corrected for {drones(new Set(warnings.map((w) => w.serial)).size)}:</p>
            <ul aria-label="Drones not expected here" className="flex flex-col gap-1.5">
              {warnings.map((w, i) => (
                <li key={`${i}-${w.serial}-${w.code}`} className="break-words rounded-lg bg-surface-container px-3 py-2 text-base text-on-surface">
                  <span className="font-mono">{w.serial}</span> {warningText(w)}
                </li>
              ))}
            </ul>
          </div>
          {error && (
            <div role="alert">
              <Notice tone="miss">{error}</Notice>
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 bg-surface px-4 pb-4 pt-2">
          <button type="button" disabled={busy} onClick={() => run(action, { confirm: true })} className={PRIMARY}>
            {busy ? 'Marking…' : `Accept and mark ${drones(list.length)}`}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setError(null);
              setStep({ name: 'confirm', action });
            }}
            className={`${BTN_TONAL} min-h-[2.75rem] w-full justify-center`}
          >
            Go back
          </button>
        </div>
      </div>,
    );
  }

  return <ResultView result={step.result} words={step.action.words} onMore={startOver} />;
};

export default MarkerFlow;
